import type {
  ChessMove,
  ChessView,
  Piece,
  PieceColor,
  PieceType,
  Square,
} from "@bing/engine-chess";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useFitSquare } from "../game/use-fit-square";
import { Avatar } from "../components/Avatar";
import { GameIcon } from "../components/GameIcon";
import { displayName, type DisplayNameParts } from "../components/profile-ui";
import { boardPieceSize } from "../game/chess-piece-size";
import { useChessSfx } from "../game/chess-sfx";
import {
  SlidePiece,
  capturedSquareOf,
  deriveCastleRook,
  displayPos,
  prefersReducedMotion,
  sanIsCapture,
  sanIsCastle,
  sanIsPromotion,
  slideDelta,
} from "../game/chess-motion";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";

/* ------------------------------------------------------------------ *
 * Chess (شطرنج) game room — the SOLO (human-vs-bot) tap-to-move board.
 * Built on the same table chrome as HokmTable/HeartsTable (a gold-framed
 * felt with an exit button top-corner) but adapted to chess: an 8×8
 * board rendered from `view.you`'s perspective (White sees rank 1 at the
 * bottom; Black sees it flipped), tap-to-move driven by the server-
 * authoritative legal moves, gold highlight states (selected glow / legal
 * dot / capture ring / last-move tint) + a red glow under a checked king,
 * an opponent + own strip with a captured-pieces tray and material badge,
 * and a compact RTL SAN move-list. Both themes look right (square colours
 * derive from the shared `card-face`/`felt-deep` tokens).
 *
 * PIECES (Phase 2 — temporary but clean): well-styled Unicode chess glyphs
 * (the FILLED set for BOTH armies, distinguished by colour — gold/cream
 * White, charcoal Black, each with a thin contrasting outline so it reads
 * on ivory AND felt-green). All piece rendering funnels through the single
 * {@link ChessPiece} helper, so Phase 4 can swap in a real SVG set in ONE
 * place.
 * ------------------------------------------------------------------ */

/** Latin→Persian digits, to match the rest of the app (move numbers, badges). */
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
function faNum(value: number | string): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/** ms → `m:ss` in Persian digits (a chess clock reads minutes:seconds, seconds zero-padded). */
function fmtClock(ms: number): string {
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${faNum(m)}:${faNum(String(s).padStart(2, "0"))}`;
}

/** CHESS ONLY — the server clock banks for a timed online table (see the game-server's `clock`
 *  frame). Seat 0 = White (`whiteMs`), seat 1 = Black (`blackMs`). */
export interface ChessClock {
  whiteMs: number;
  blackMs: number;
  /** Server epoch (ms) at which the side-to-move's turn began. */
  turnStartedAt: number;
  /** The side to move (0 = White, 1 = Black) — its clock ticks; the other's is frozen. */
  turn: number;
}

/**
 * A per-side time-bank chip. The side to move (`running`) counts down smoothly from the server
 * snapshot (`ms − (now − since)`); the other side is frozen at its bank. Turns red under 10s. The
 * SERVER is authoritative on flag-fall — this is display only, clamped at 0:00.
 */
function ClockChip({
  ms,
  running,
  since,
}: {
  ms: number;
  running: boolean;
  since: number;
}): ReactNode {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);
  const remaining = running ? Math.max(0, ms - (now - since)) : Math.max(0, ms);
  const low = remaining <= 10_000;
  return (
    <span
      aria-label={fa.chess.clock}
      className={`rounded-lg border px-2.5 py-1 font-numeral text-body-lg tabular-nums ${
        low
          ? "border-danger-bluff/50 bg-danger-bluff/15 text-danger-bluff"
          : running
            ? "border-secondary/60 bg-secondary/20 text-secondary"
            : "border-outline-variant bg-surface-container/70 text-on-surface"
      }`}
      style={{ fontWeight: 800, letterSpacing: "0.02em" }}
    >
      {fmtClock(remaining)}
    </span>
  );
}

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

/** Standard relative piece values — drive the captured-tray material badge. */
const PIECE_VALUE: Record<PieceType, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

/** Persian piece names — used by the promotion picker's option labels. */
const PIECE_NAME: Record<"q" | "r" | "b" | "n", string> = {
  q: fa.chess.queen,
  r: fa.chess.rook,
  b: fa.chess.bishop,
  n: fa.chess.knight,
};

/** The piece SVG set — a premium, original **CC0** "casino" set (gold White / charcoal
 * Black), `public/pieces/casino/<code>.svg` where `<code>` is the engine's 2-char id
 * (`wK`…`bP`). See `public/pieces/ATTRIBUTION.md`. The army colours are baked in (they are
 * chess SEMANTICS — the light vs. dark army — constant across the app's light/dark THEME),
 * and each carries a contrasting outline so it reads on all four square colours. */
const PIECE_SET = "casino";

/** The art's version on the URL. /pieces/ has no Cache-Control, and Cloudflare and the WebView keep an `.svg` by URL,
 *  so art redrawn in place needs a NEW url to reach a client that holds the old one. 6 = item 13's refinement of
 *  prod's (fbb8d48's) drawing. NEVER reuse 2–5: staging served card-60's item 6 and chess-colors' art under them. */
const PIECE_ART_VERSION = 6;

/** THE single place a chess piece is drawn — swap `PIECE_SET` (or this element) to change
 * the whole game's pieces; nothing else in the table renders one. Loads the set's SVG via
 * an `<img>` (lazy, cached), so solo + online chess share one set. `className`/`style` let a
 * caller layer a motion (a promotion pop-in, the mated-king topple). */
export function ChessPiece({
  type,
  color,
  size,
  className,
  style,
}: {
  type: PieceType;
  color: PieceColor;
  /** Rendered box size — a number (px) or any CSS length (e.g. a `clamp(…)`). */
  size: number | string;
  className?: string;
  style?: CSSProperties;
}): ReactNode {
  const px = typeof size === "number" ? `${size}px` : size;
  return (
    <img
      aria-hidden
      alt=""
      draggable={false}
      data-chess-piece={`${color}${type}`}
      src={`/pieces/${PIECE_SET}/${color}${type.toUpperCase()}.svg?v=${PIECE_ART_VERSION}`}
      className={className}
      style={{
        display: "block",
        width: px,
        height: px,
        userSelect: "none",
        pointerEvents: "none",
        // A soft drop shadow lifts the piece off both the ivory and felt-green squares
        // (replaces the glyph's text-shadow), tuned the same across both themes.
        filter: "drop-shadow(0 2px 3px rgba(0,0,0,.45))",
        ...style,
      }}
    />
  );
}

/** Read a piece off a raw 64-cell board (index = rank*8 + file) by algebraic square. */
function pieceOn(board: readonly (Piece | null)[], sq: Square): Piece | null {
  const file = sq.charCodeAt(0) - 97;
  const rank = Number(sq[1]) - 1;
  return board[rank * 8 + file] ?? null;
}

/** The square of `color`'s king (for the checkmate topple) — null if somehow absent. */
function findKingSquare(board: readonly (Piece | null)[], color: PieceColor): Square | null {
  for (let i = 0; i < 64; i += 1) {
    const p = board[i];
    if (p && p.type === "k" && p.color === color) {
      return `${FILES[i % 8] ?? "a"}${Math.floor(i / 8) + 1}`;
    }
  }
  return null;
}

/** Per-square motion instructions, computed once in `ChessTable` (which knows the move + the
 * reduced-motion preference) and threaded into `BoardSquare` so the square stays presentational. */
interface SquareFx {
  /** The mover's slide-in delta (in squares); present only on a moved-to square. */
  slide?: { dx: number; dy: number };
  /** Remount key so a fresh move re-plays the slide exactly once. */
  moveKey?: string;
  /** An extra class on the piece image (a promotion pop, the mated-king topple). */
  pieceClass?: string;
  /** Raise this square above its neighbours while its piece slides across them. */
  elevate?: boolean;
  /** Pulse the check red-glow (with `checkKey` remounting it per fresh check). */
  checkPulse?: boolean;
  checkKey?: string;
}

/** Base reset for every <button> (Tailwind preflight is off → reset explicitly). */
const BTN = "appearance-none m-0 cursor-pointer";

/** Neutral avatar ring (a strip's inactive state); the gold turn-glow wins when active. */
const NEUTRAL_RING = "0 0 0 2px rgb(var(--c-outline-variant)), 0 4px 8px rgba(0,0,0,.4)";

/** The signed-in user's identity — renders the OWN strip with the real profile
 * photo + display name. Omitted (solo tests) → a preset avatar + «شما». */
export interface ChessSelfIdentity extends DisplayNameParts {
  avatarId?: string | null;
  avatarPhotoId?: string | null;
  /** Official/verified account → the disc wears the badge. */
  isOfficial?: boolean;
}

/** Rank/file of a square string ("e4" → { file: 4, rank: 3 }, 0-based). */
function squareCoords(sq: Square): { file: number; rank: number } {
  return { file: sq.charCodeAt(0) - 97, rank: Number(sq[1]) - 1 };
}

/** ITEM 8 — the captured glyph size. The reserved tray box is exactly two of these tall, so the
 *  two must move together; measured to fit all fifteen a side can lose at 320px and up. */
const CAPTURED_ROW_PX = 22;

/** The captured-pieces row (ROW 2 of the player strip) — glyphs of one colour, biggest first,
 * at a chunky {@link CAPTURED_ROW_PX}px. Fills its parent, a RESERVED two-row-tall box (`flex-wrap` + `content-start`):
 * the top row fills, then the second, overflow hidden past two — so accumulating captures never
 * resize the strip or shift the board. Identical on Android + iOS.
 *
 * `data-chess-tray` names the army it holds: in the DARK theme `theme.css` puts each captured BLACK piece on a tile
 * of the board's light square (item 13) — paint only, on the piece's own box, so nothing here moves. */
function CapturedTray({
  pieces,
  color,
}: {
  pieces: readonly PieceType[];
  color: PieceColor;
}): ReactNode {
  const sorted = [...pieces].sort((a, b) => PIECE_VALUE[b] - PIECE_VALUE[a]);
  return (
    <span
      aria-label={fa.chess.captured}
      data-chess-tray={color}
      className="flex h-full w-full flex-wrap content-start items-center overflow-hidden"
      style={{ gap: 3, rowGap: 2 }}
    >
      {sorted.map((t, i) => (
        <ChessPiece key={i} type={t} color={color} size={CAPTURED_ROW_PX} />
      ))}
    </span>
  );
}

/** The `+N` material-advantage badge (rendered only when this side is ahead). */
function MaterialBadge({ advantage }: { advantage: number }): ReactNode {
  if (advantage <= 0) return null;
  return (
    <span
      className="rounded-full border border-secondary/40 bg-secondary/20 px-1.5 font-caption text-caption text-secondary"
      style={{ fontWeight: 800 }}
    >
      {`+${faNum(advantage)}`}
    </span>
  );
}

/** A monospace stack for the move-list SAN tokens — gives the notation its tidy "chess
 *  score-sheet" feel (the app otherwise ships only Vazirmatn, which isn't monospaced). */
const SAN_MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** One SAN half-move token in the move list. White's reads a touch brighter than Black's,
 *  and the LATEST ply played wears a gold chip so the eye finds "where we are" at a glance.
 *  A constant-width (transparent) border on the non-latest state keeps a token from shifting
 *  its neighbours when it becomes — or stops being — the latest. */
function MoveToken({
  san,
  color,
  latest,
}: {
  san: string;
  color: "w" | "b";
  latest: boolean;
}): ReactNode {
  return (
    <span
      data-latest={latest ? "true" : undefined}
      data-san-color={color}
      className={`rounded-md border px-1.5 py-0.5 text-caption transition-colors ${
        latest
          ? "border-secondary/45 bg-secondary/20 text-secondary"
          : color === "b"
            ? "border-transparent text-on-surface-variant"
            : "border-transparent text-on-surface"
      }`}
      style={{ fontFamily: SAN_MONO, fontWeight: latest ? 800 : 600, lineHeight: 1.15 }}
    >
      {san}
    </span>
  );
}

/** One player strip: avatar + name + material badge, with the captured tray (or a
 * status hint) beneath. The active side wears the gold turn-glow ring. */
function PlayerStrip({
  name,
  avatarId,
  avatarPhotoId,
  official,
  tray,
  trayColor,
  advantage,
  active,
  clock,
  dimmed,
  tag,
}: {
  name: string;
  avatarId: string;
  avatarPhotoId: string | null;
  /** Official/verified account → the disc wears the badge. */
  official?: boolean;
  tray: readonly PieceType[];
  trayColor: PieceColor;
  advantage: number;
  active: boolean;
  /** The time-bank chip (timed online tables); omitted for solo / untimed → the strip has no clock. */
  clock?: ReactNode;
  /** THIS CHAIR IS EMPTY — fade the avatar + name so the seat reads as vacated at a glance. The
   *  clock is deliberately NOT faded: it is information, and on an absent seat it is the one
   *  thing that has to stay readable (it is stopped, and saying so is the point). */
  dimmed?: boolean;
  /** A small marker beside the name (the «سر میز نیست» chip on an absent seat). */
  tag?: ReactNode;
}): ReactNode {
  // ITEM 8 — TWO ROWS: the profile (avatar + name + material + clock) on top, the captured tray
  // on its OWN full-width row beneath.
  //
  // They never OVERLAPPED — flex items cannot — but the tray was a `flex-1` column competing for
  // width with the profile and the clock, and it lost: measured at 390px with a clock the box was
  // 109px, so of the fifteen pieces a side can lose only EIGHT were inside it; at 320px, two. The
  // rest wrapped past the reserved two rows and `overflow-hidden` simply cut them off, silently.
  // Given the whole strip's width the same fifteen fit in two rows at every size measured
  // (320/360/390/430, clock or not) at the full chunky 22px. The reserved height is unchanged in
  // kind — still fixed, so accumulating captures never resize the strip or shift the board.
  //
  // Costs ~42px per strip. The board is capped at 400px and width-limited on any normal phone, so
  // it is unaffected there; only a very short screen (~568px tall) gives up a little board.
  //
  // No «thinking» text (it toggles every move and would shift the table); whose turn it is shows
  // via the avatar's gold glow (`active`) instead.
  return (
    <div className="flex flex-col gap-1">
      {/* ROW 1 — profile: avatar + name + material badge + the clock. The name FLEXES and
          truncates, so a long one shortens itself instead of pushing the clock off. */}
      <div className="flex items-center gap-2">
        <div
          className="flex flex-none items-center justify-center rounded-full"
          style={{
            padding: 2,
            boxSizing: "border-box",
            boxShadow: active
              ? "0 0 14px 3px rgb(var(--c-secondary) / .4), 0 0 0 2px rgb(var(--c-secondary))"
              : NEUTRAL_RING,
            animation: active ? "turn-glow-pulse 1.6s ease-in-out infinite" : undefined,
            opacity: dimmed ? 0.55 : undefined,
          }}
        >
          <Avatar avatarId={avatarId} avatarPhotoId={avatarPhotoId} size={36} official={official} />
        </div>
        <span
          className="m-0 min-w-0 flex-1 truncate font-body-lg text-body-lg text-on-surface"
          style={dimmed ? { opacity: 0.55 } : undefined}
        >
          {name}
        </span>
        {tag ? <div className="flex-none">{tag}</div> : null}
        <MaterialBadge advantage={advantage} />
        {clock ? <div className="flex-none">{clock}</div> : null}
      </div>
      {/* ROW 2 — captured pieces across the FULL width, in a reserved two-row box (2 × 22px + the
          row gap). Fixed height: accumulating captures never resize the strip or shift the board. */}
      <div className="w-full" style={{ height: CAPTURED_ROW_PX * 2 + 2 }}>
        <CapturedTray pieces={tray} color={trayColor} />
      </div>
    </div>
  );
}

/** One board square: base colour + highlight overlays + centred piece + quiet
 * edge coordinate. A <button> so it's tappable/accessible; disabled (so a tap
 * is inert) while the board is read-only (not your turn / game over). */
function BoardSquare({
  square,
  piece,
  isLight,
  interactive,
  isSelected,
  isTarget,
  isCapture,
  isLastMove,
  isCheck,
  fileLabel,
  rankLabel,
  onTap,
  fx,
  isShaking,
  pieceSize,
}: {
  square: Square;
  piece: Piece | null;
  isLight: boolean;
  interactive: boolean;
  isSelected: boolean;
  isTarget: boolean;
  isCapture: boolean;
  isLastMove: boolean;
  isCheck: boolean;
  fileLabel?: string;
  rankLabel?: string;
  onTap: (sq: Square) => void;
  /** Motion instructions for this square (see {@link SquareFx}); omitted → a static square. */
  fx?: SquareFx;
  /** The illegal-attempt shake (§11 #10) — this square jitters briefly. */
  isShaking?: boolean;
  /** The piece's box — sized by THIS square (`boardPieceSize`, item 13): as big as the square comfortably allows,
   *  never smaller than prod's `clamp(20px, 8vw, 34px)`. */
  pieceSize: string;
}): ReactNode {
  // Ivory light squares + deep felt-teal-green dark squares — both from the shared
  // tokens, so the board reads on both themes (dark: ivory #fbfaf4 / teal #134e38;
  // light: white / mint, tying the board to the mint felt).
  const bg = isLight ? "rgb(var(--c-card-face))" : "rgb(var(--c-felt-deep))";
  const coordColor = isLight ? "rgb(var(--c-gold-deep))" : "rgb(var(--c-secondary) / .85)";
  return (
    <button
      type="button"
      aria-label={square}
      data-square={square}
      data-piece={piece ? `${piece.color}${piece.type}` : undefined}
      data-selected={isSelected ? "true" : undefined}
      data-target={isTarget ? "true" : undefined}
      data-check={isCheck ? "true" : undefined}
      disabled={!interactive}
      onClick={() => onTap(square)}
      className={`${BTN} relative border-0 p-0${isShaking ? " chess-square-shake" : ""}`}
      style={{
        aspectRatio: "1",
        // A whisper of top-left-light → bottom-right-dark over the base colour gives the felt some
        // depth/sheen (richer than a flat fill) without touching the piece or highlight layers.
        background: `linear-gradient(155deg, rgba(255, 255, 255, 0.06), rgba(0, 0, 0, 0.08)), ${bg}`,
        cursor: interactive ? "pointer" : "default",
        // A sliding piece overflows its square, so lift the moved-to square above its
        // neighbours for the flight (positioned siblings stack by z-index).
        zIndex: fx?.elevate ? 30 : undefined,
        boxShadow: isSelected
          ? "inset 0 0 0 3px rgb(var(--c-secondary)), 0 0 12px 2px rgb(var(--c-secondary) / .55)"
          : undefined,
      }}
    >
      {/* Last-move: a soft gold tint on both squares. */}
      {isLastMove ? (
        <span
          className="pointer-events-none absolute inset-0"
          style={{ background: "rgb(var(--c-secondary) / .28)" }}
        />
      ) : null}
      {/* Check: a red glow under the checked king (pulses ~2× when the check is fresh). */}
      {isCheck ? (
        <span
          key={fx?.checkKey}
          className={`pointer-events-none absolute inset-0${fx?.checkPulse ? " chess-check-pulse" : ""}`}
          style={{
            background:
              "radial-gradient(circle, rgb(var(--c-danger-bluff) / .9) 0%, rgb(var(--c-danger-bluff) / .25) 55%, transparent 72%)",
          }}
        />
      ) : null}
      {/* The piece (single render path — ChessPiece), wrapped in a slide layer so a move's
          mover flies in from its origin square (keyed by the move → plays exactly once). */}
      {piece ? (
        <SlidePiece
          key={fx?.slide ? fx.moveKey : "static"}
          dx={fx?.slide?.dx ?? 0}
          dy={fx?.slide?.dy ?? 0}
          animate={fx?.slide != null}
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <ChessPiece
            type={piece.type}
            color={piece.color}
            size={pieceSize}
            className={fx?.pieceClass}
          />
        </SlidePiece>
      ) : null}
      {/* Legal move: a gold dot on an empty square, a gold ring around a capture. */}
      {isTarget && !isCapture ? (
        <span
          className="pointer-events-none absolute rounded-full"
          style={{
            left: "50%",
            top: "50%",
            width: "30%",
            height: "30%",
            transform: "translate(-50%, -50%)",
            background: "rgb(var(--c-secondary) / .8)",
          }}
        />
      ) : null}
      {isTarget && isCapture ? (
        <span
          className="pointer-events-none absolute rounded-full"
          style={{ inset: "7%", border: "3px solid rgb(var(--c-secondary))" }}
        />
      ) : null}
      {/* Quiet edge coordinates (a–h along the bottom, 1–8 up the side). */}
      {rankLabel ? (
        <span
          className="pointer-events-none absolute"
          style={{ top: 2, left: 3, fontSize: 9, fontWeight: 700, color: coordColor }}
        >
          {rankLabel}
        </span>
      ) : null}
      {fileLabel ? (
        <span
          className="pointer-events-none absolute"
          style={{ bottom: 1, right: 3, fontSize: 9, fontWeight: 700, color: coordColor }}
        >
          {fileLabel}
        </span>
      ) : null}
    </button>
  );
}

/** The promotion picker — a small centred overlay offering Q/R/B/N in the mover's
 * colour. Shown when a pawn reaches the last rank; the pick supplies `promo`. */
function PromoPicker({
  color,
  onPick,
  onCancel,
}: {
  color: PieceColor;
  onPick: (promo: "q" | "r" | "b" | "n") => void;
  onCancel: () => void;
}): ReactNode {
  return (
    <div
      role="dialog"
      aria-label={fa.chess.promotion}
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.55)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl border border-outline-variant bg-surface-container-high p-4 shadow-2xl"
        style={{ width: "100%", maxWidth: 300, textAlign: "center" }}
      >
        <div className="mb-3 font-body-lg text-body-lg text-on-surface" style={{ fontWeight: 700 }}>
          {fa.chess.promotionPrompt}
        </div>
        <div className="flex items-center justify-center gap-2">
          {(["q", "r", "b", "n"] as const).map((p) => (
            <button
              key={p}
              type="button"
              aria-label={PIECE_NAME[p]}
              onClick={() => onPick(p)}
              className={`${BTN} flex h-14 w-14 items-center justify-center rounded-xl border border-outline-variant bg-surface-container transition-all hover:-translate-y-0.5 hover:border-secondary active:scale-95`}
            >
              <ChessPiece type={p} color={color} size={30} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ChessTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  opponent,
  clock,
  opponentAbsent = false,
  opponentAway = false,
  leaveIsFree = false,
}: {
  view: ChessView;
  legal: readonly ChessMove[];
  onMove: (move: ChessMove) => void;
  onLeave: () => void;
  self?: ChessSelfIdentity | null;
  /** The opponent's identity — threaded ONLINE so a human opponent shows their real
   *  name/avatar. Omitted (solo, or a bot-fill seat) → the default «ربات» + wolf preset. */
  opponent?: ChessSelfIdentity | null;
  /** CHESS ONLY — the server clock banks for a TIMED online table. Omitted / null (solo, untimed
   *  online) → no clock chips are shown at all. */
  clock?: ChessClock | null;
  /**
   * ONLINE RESUME — the opponent's chair is EMPTY: this game was resumed and they have not come
   * back yet. Distinct from every other reason the other side is quiet — a bot fill (which IS a
   * «ربات»), a drop (which pauses and shows its own overlay), a walk-out (which ends the game).
   * Drives three things: the chip names them instead of the solo default, both clocks read
   * STOPPED (the server has frozen them — nobody can flag while a chair is empty), and the notice
   * below the strip explains it. False/omitted ⇒ the table renders exactly as it always has.
   */
  opponentAbsent?: boolean;
  /**
   * ONLINE — the opponent is NOT AT THE BOARD RIGHT NOW: their socket dropped and the table is
   * stopped for them. Deliberately separate from {@link opponentAbsent}, which means the chair
   * holds NOBODY (a resumed game): here the chair is still theirs, they may be seconds from
   * returning, and the full-screen away overlay is already saying so — so this drives ONE thing,
   * the clock.
   *
   * ⚠️ AND IT HAS TO, because the two halves of a frozen clock live in different processes. The
   * server settles both banks at the drop; the chip extrapolates `bank − (now − turnStartedAt)`
   * from its last snapshot and would otherwise go on falling through the whole pause — measured on
   * the deployed staging server as ~414s on screen against a real bank of 539.9s. The server also
   * broadcasts the settled bank now (`pauseSeat`), and the two fixes are independent on purpose: a
   * stale bundle still gets the right NUMBER, and a new bundle on an old server still STOPS.
   */
  opponentAway?: boolean;
  /** ONLINE — leaving right now records nothing. Shown ON THE TABLE beside the absent-seat notice,
   *  because it was correct in the confirm dialog and invisible until you opened it. */
  leaveIsFree?: boolean;
}): ReactNode {
  const you = view.you;
  const myColor: PieceColor = you === 0 ? "w" : "b";
  const oppColor: PieceColor = myColor === "w" ? "b" : "w";
  // The board is read-only unless it's genuinely your move to make. When it's the
  // bot's turn `view.legalTargets` describes ITS options, so we ignore it entirely.
  const isMyTurn = view.turn === you && view.status === "playing";

  // The OS "reduce motion" preference — recomputed each render; gates every board motion
  // in JSX (so a fill-mode animation never strands a hidden piece).
  const reduced = prefersReducedMotion();

  const [selected, setSelected] = useState<Square | null>(null);
  // The board is sized to the ACTUAL remaining box (the smaller of its width/height) so it can
  // never overflow the chrome (profile strips + captured trays + move list) on a short viewport —
  // this is the fix for the board overlapping the profile/captured area. Cap 400; pad 16 leaves
  // room for the board's gold frame + shadow inside the box.
  const fit = useFitSquare<HTMLElement>(400, 16);
  // ITEM 13 (reopened) — every board piece, and the capture ghost, is sized by its square (the board is 8 × 8 equal
  // tracks of `fit.size`): as big as the square comfortably allows, never smaller than prod's box.
  const pieceSize = boardPieceSize(fit.size / 8);
  const [promo, setPromo] = useState<{ from: Square; to: Square } | null>(null);
  // The illegal-attempt shake (§11 #10) — a square jitters, then clears itself.
  const [shakeSquare, setShakeSquare] = useState<Square | null>(null);
  // Any position change (your move, the bot's reply, a fresh game) clears the local
  // selection/promo — the next authoritative view is the truth.
  useEffect(() => {
    setSelected(null);
    setPromo(null);
  }, [view.fen, isMyTurn]);

  // Chess move sounds — one cue per completed move (both solo + online; see game/chess-sfx).
  useChessSfx(view.moveList);

  // Suppress replaying the last move's motions on the very FIRST mount (a resume).
  const firstRenderRef = useRef(true);
  useEffect(() => {
    firstRenderRef.current = false;
  }, []);
  // The board as it was one render ago (the PRE-move board) — used to find a captured piece.
  // `view.board` only changes on a real move, so a clock re-render keeps the pre-move board.
  const prevBoardRef = useRef<readonly (Piece | null)[]>(view.board);
  useEffect(() => {
    prevBoardRef.current = view.board;
  }, [view.board]);

  const pieceAt = (sq: Square): Piece | null => {
    const { file, rank } = squareCoords(sq);
    return view.board[rank * 8 + file] ?? null;
  };

  // A friendly pawn reaching its last rank is ALWAYS a promotion; `legal` (the
  // server's authoritative move list — populated only on your turn) confirms it
  // when present, and the geometric check covers a hand-built fixture without it.
  const isPromotionMove = (from: Square, to: Square): boolean => {
    if (legal.some((m) => m.from === from && m.to === to && m.promo != null)) return true;
    const p = pieceAt(from);
    const toRank = squareCoords(to).rank;
    return (
      !!p &&
      p.type === "p" &&
      ((p.color === "w" && toRank === 7) || (p.color === "b" && toRank === 0))
    );
  };

  /**
   * THE KING, THEN THAT WING'S OWN ROOK, CASTLES (the operator, 2026-11-02) — the same move as tapping the
   * king's two-square target, and only when `view.legalTargets` already lists that step, so the rook tap
   * and the gold dot can never disagree (the board still has no chess rules of its own). The rook must be
   * yours, on the king's rank, in its wing's corner (h → the king's side, a → the queen's). Anything else
   * is `null`, and the tap does what it always did: the rook is re-selected, or the illegal shake.
   */
  const castleViaRook = (from: Square, to: Square): Square | null => {
    const king = pieceAt(from);
    const rook = pieceAt(to);
    if (king?.type !== "k" || king.color !== myColor) return null;
    if (rook?.type !== "r" || rook.color !== myColor) return null;
    const k = squareCoords(from);
    const r = squareCoords(to);
    const step = r.file === 7 ? 2 : r.file === 0 ? -2 : null;
    if (step === null || r.rank !== k.rank) return null;
    const target = `${FILES[k.file + step] ?? ""}${k.rank + 1}`;
    return (view.legalTargets[from] ?? []).includes(target) ? target : null;
  };

  const targets: readonly Square[] =
    isMyTurn && selected ? (view.legalTargets[selected] ?? []) : [];

  const onTap = (sq: Square): void => {
    if (!isMyTurn || promo) return;
    if (selected) {
      if (sq === selected) {
        setSelected(null);
        return;
      }
      if ((view.legalTargets[selected] ?? []).includes(sq)) {
        if (isPromotionMove(selected, sq)) {
          setPromo({ from: selected, to: sq });
          return;
        }
        onMove({ from: selected, to: sq });
        setSelected(null);
        return;
      }
      const castleTo = castleViaRook(selected, sq);
      if (castleTo) {
        onMove({ from: selected, to: castleTo });
        setSelected(null);
        return;
      }
      // A piece is selected and the tap is neither its own square nor a legal target. If it
      // is ANOTHER of your pieces we re-select (below); otherwise it is an ILLEGAL
      // destination — a subtle tick + a brief shake of the selected square, then snap back.
      if (!view.legalTargets[sq]) {
        playSfx("chessIllegal");
        if (!reduced) {
          setShakeSquare(selected);
          window.setTimeout(() => setShakeSquare(null), 240);
        }
        setSelected(null);
        return;
      }
    }
    // (Re)select — only a square holding one of YOUR movable pieces has legal targets.
    if (view.legalTargets[sq]) {
      setSelected(sq);
      return;
    }
    setSelected(null);
  };

  const pickPromo = (p: "q" | "r" | "b" | "n"): void => {
    if (!promo) return;
    onMove({ from: promo.from, to: promo.to, promo: p });
    setPromo(null);
    setSelected(null);
  };

  // ── Captured trays + material advantage ──────────────────────────────────
  // `captured.w` = White pieces taken (so Black's spoils); `captured.b` = Black's.
  // MY tray is the enemy pieces I took (rendered in the enemy's colour).
  const myTray = you === 0 ? view.captured.b : view.captured.w;
  const oppTray = you === 0 ? view.captured.w : view.captured.b;
  const materialOf = (tray: readonly PieceType[]): number =>
    tray.reduce((sum, t) => sum + PIECE_VALUE[t], 0);
  const advantage = materialOf(myTray) - materialOf(oppTray);

  // ── Clock chips (TIMED online only) ──────────────────────────────────────
  // you=0 → my bank is White's; the side to move (clock.turn) ticks, and only while the game is live.
  // Omitted entirely for solo / untimed (no `clock`), so those tables look exactly as before.
  const gameLive = view.status === "playing";
  /*
   * ⚠️ AN ABSENT CHAIR STOPS **BOTH** CLOCKS, NOT JUST THEIRS — that is what the server holds.
   * `armChessClock` freezes on `!everySeatOccupied()`: it arms no flag for anyone, keeps the
   * banks and resets the stopwatch, so while a seat is empty NOBODY's time is running. The chip
   * ticks locally from `bank − (now − turnStartedAt)` and nothing re-broadcasts while frozen, so
   * a stopped clock counted all the way down to 0:00 on screen — telling one player her opponent
   * was thinking, for 25 minutes, when there was nobody there (2026-09-04).
   *
   * ⚠️ AND A DROPPED SEAT IS THE SAME PICTURE THROUGH A DIFFERENT DOOR (2026-09-14). The server
   * freezes at the drop too — `markDropped` → `pauseSeat` → `freezeChessClock` — but for a
   * DROPPED seat `everySeatOccupied()` is still true, so the reasoning above does not reach it and
   * the chip went on falling behind the away overlay. Measured on staging: ~414s on screen against
   * a bank of 539.9s. Two reasons, one behaviour; see {@link opponentAway} for why they stay two
   * props rather than one.
   */
  const clocksFrozen = opponentAbsent || opponentAway;
  const myClock = clock ? (
    <ClockChip
      ms={you === 0 ? clock.whiteMs : clock.blackMs}
      since={clock.turnStartedAt}
      running={gameLive && !clocksFrozen && clock.turn === you}
    />
  ) : undefined;
  const oppClock = clock ? (
    <ClockChip
      ms={you === 0 ? clock.blackMs : clock.whiteMs}
      since={clock.turnStartedAt}
      running={gameLive && !clocksFrozen && clock.turn !== you}
    />
  ) : undefined;

  // ── Status line (also the check / checkmate / draw text the table shows) ──
  const statusText =
    view.status === "checkmate"
      ? fa.chess.checkmate
      : view.status === "stalemate"
        ? fa.chess.stalemate
        : view.status === "draw"
          ? fa.chess.draw
          : view.check
            ? fa.chess.check
            : isMyTurn
              ? fa.chess.yourTurn
              : fa.chess.opponentTurn;
  const statusDanger = !!view.check || view.status === "checkmate";
  const statusGold = view.status === "playing" && isMyTurn && !view.check;
  const statusPillClass = statusDanger
    ? "border-danger-bluff/50 bg-danger-bluff/15 text-danger-bluff"
    : statusGold
      ? "border-secondary/50 bg-secondary/20 text-secondary animate-pulse"
      : "border-outline-variant bg-surface-container/70 text-on-surface-variant";

  // ── Own seat identity (real profile, else a preset + «شما») ──────────────
  const ownName = ((): string => {
    const name = self ? displayName(self) : "";
    return name && name !== fa.friends.user ? name : fa.chess.you;
  })();
  const ownAvatarId = self?.avatarId ?? "fox";
  const ownAvatarPhoto = self?.avatarPhotoId ?? null;

  // ── Opponent seat identity (real profile ONLINE, else a preset + «ربات») ──
  /*
   * ⚠️ THE DEFAULT IS «ربات» AND IT IS ONLY TRUE ONCE A BOT REALLY SITS DOWN. On a resumed game
   * the other chair belongs to a named person who is not here yet, and showing the SOLO fallback
   * there is the one case that most needs its own label. When the server could not resolve the
   * name it falls to «حریف» — the word the turn line already uses — never to «ربات».
   */
  const oppName = ((): string => {
    const name = opponent ? displayName(opponent) : "";
    if (name && name !== fa.friends.user) return name;
    return opponentAbsent ? fa.chess.awayName : fa.chess.opponent;
  })();
  const oppAvatarId = opponent?.avatarId ?? "wolf";
  const oppAvatarPhoto = opponent?.avatarPhotoId ?? null;

  // ── SAN move list, grouped into full moves ───────────────────────────────
  const moveRows: { n: number; w: string; b?: string }[] = [];
  for (let i = 0; i < view.moveList.length; i += 2) {
    moveRows.push({ n: i / 2 + 1, w: view.moveList[i] ?? "", b: view.moveList[i + 1] });
  }
  const moveListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = moveListRef.current;
    // SAN is LTR, so the latest move sits at the right end — keep it in view.
    if (el) el.scrollLeft = el.scrollWidth;
  }, [view.moveList.length]);

  const topPad = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";

  // ── Last-move "exclamations" (design spec §11) ───────────────────────────
  // All derived at render time from the authoritative view + the pre-move board, and keyed
  // (below) so each beat plays exactly once. `animateMove` is false on the first mount (a
  // resume) and under reduced motion, so those paths never animate.
  const lastMove = view.lastMove;
  const moveCount = view.moveList.length;
  const lastSan = moveCount > 0 ? (view.moveList[moveCount - 1] ?? "") : "";
  const moveKey = lastMove ? `${lastMove.from}-${lastMove.to}-${moveCount}` : "none";
  const animateMove = !firstRenderRef.current && lastMove != null && !reduced;
  // A promotion pops the chosen piece in (no slide); every other move slides its mover in.
  const promoSquare = animateMove && lastMove && sanIsPromotion(lastSan) ? lastMove.to : null;
  const moverSlide =
    animateMove && lastMove && !promoSquare ? slideDelta(lastMove.from, lastMove.to, you) : null;
  // Castling slides the rook alongside the king.
  const castleRook =
    animateMove && lastMove && sanIsCastle(lastSan)
      ? deriveCastleRook(lastMove.from, lastMove.to)
      : null;
  const rookSlide = castleRook ? slideDelta(castleRook.from, castleRook.to, you) : null;
  // A capture leaves a shrinking, fading ghost of the taken piece where it stood. Derived
  // from the PRE-move board; the mover-colour guard drops the stale recompute a later
  // (clock) re-render would produce against the post-move board.
  const capturedGhost = ((): { piece: Piece; col: number; row: number } | null => {
    if (!animateMove || !lastMove || !sanIsCapture(lastSan)) return null;
    const prev = prevBoardRef.current;
    const capSq = capturedSquareOf(
      lastMove.from,
      lastMove.to,
      lastSan,
      (s) => pieceOn(prev, s) != null,
    );
    if (!capSq) return null;
    const captured = pieceOn(prev, capSq);
    if (!captured) return null;
    const mover = pieceOn(view.board, lastMove.to);
    if (mover && captured.color === mover.color) return null; // stale (post-move) — not a capture
    const pos = displayPos(capSq, you);
    return { piece: captured, col: pos.col, row: pos.row };
  })();
  // Checkmate: the mated king (the side to move at mate = the loser) desaturates + topples.
  const matedColor: PieceColor | null =
    view.status === "checkmate"
      ? (view.winner != null ? 1 - view.winner : view.turn) === 0
        ? "w"
        : "b"
      : null;
  const matedKingSquare = matedColor ? findKingSquare(view.board, matedColor) : null;
  // Remount the checked king's glow per fresh checking position so its pulse replays.
  const checkKey = `${view.check ?? "-"}-${view.fen}`;

  return (
    <div
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — exit (right in RTL) · centred «شطرنج» + status · balancing spacer. */}
      <header data-notice-band="" className="relative z-10 px-4" style={{ paddingTop: topPad }}>
        {/* items-start pins the exit button to the top of the band (top = topPad) so it lines up
            with the online chat toggle (fixed at top:10px+safe), instead of centering against the
            taller title+status column and sitting ~7px lower. */}
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            aria-label={fa.chess.leave}
            onClick={onLeave}
            className={`${BTN} flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>
          <div className="flex flex-col items-center gap-1">
            <h1 className="m-0 font-headline-md text-headline-md text-secondary">
              {fa.chess.title}
            </h1>
            <span
              className={`m-0 rounded-full border px-3 py-0.5 font-label-md text-label-md ${statusPillClass}`}
            >
              {statusText}
            </span>
          </div>
          {/* Spacer, matching the exit button, so the title stays truly centred. */}
          <div className="h-10 w-10 flex-none" />
        </div>
      </header>

      {/* Opponent strip — the bot (solo) or the real online opponent when threaded. Sits a bit
          lower (pt-4) so it clears the HUD. */}
      <div className="px-4 pt-4">
        <PlayerStrip
          name={oppName}
          avatarId={oppAvatarId}
          avatarPhotoId={oppAvatarPhoto}
          official={opponent?.isOfficial === true}
          tray={oppTray}
          trayColor={myColor}
          advantage={-advantage}
          active={!isMyTurn && view.status === "playing" && !opponentAbsent}
          clock={oppClock}
          dimmed={opponentAbsent}
          tag={
            opponentAbsent ? (
              <span
                data-testid="chess-away-tag"
                className="rounded-full border border-outline-variant bg-surface-container/70 px-2 py-0.5 text-on-surface-variant"
              >
                {fa.chess.awayTag}
              </span>
            ) : null
          }
        />
      </div>

      {/* THE OTHER CHAIR IS EMPTY — say so on the table itself. Everything a player needs to
          decide what to do was previously either wrong (the chip said «ربات», the clock ticked)
          or one tap inside a dialog they had no reason to open. The free-exit line is the half
          that matters most and it is the last one, where the eye lands. */}
      {opponentAbsent ? (
        <div className="px-4 pt-2">
          <div
            data-testid="chess-away-notice"
            className="rounded-xl border border-outline-variant bg-surface-container/70 px-3 py-2 text-center"
          >
            <p className="m-0 text-on-surface">{fa.chess.awayTitle}</p>
            <p className="m-0 mt-0.5 font-label-md text-label-md text-on-surface-variant">
              {fa.chess.awayBody}
            </p>
            {leaveIsFree ? (
              <p
                data-testid="chess-away-free-exit"
                className="m-0 mt-1 font-label-md text-label-md text-secondary"
              >
                {fa.chess.awayFreeExit}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Board — the centrepiece. `dir="ltr"` so files always lay out left→right
          regardless of the app's RTL direction. */}
      <main
        ref={fit.ref}
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-2 py-1"
      >
        <div
          dir="ltr"
          aria-label="chess board"
          style={{
            position: "relative", // anchors the capture-ghost overlay
            width: fit.size,
            height: fit.size,
            borderRadius: 0,
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gridTemplateRows: "repeat(8, 1fr)",
            // A richer double frame: a gold ring seated in a warm wood-brown ring, a stronger gold
            // halo, and a deeper drop shadow — reads more premium than a single gold line.
            boxShadow:
              "0 0 0 3px rgb(var(--c-gold-deep)), 0 0 0 6px rgba(43,24,16,.85), 0 0 30px 7px rgb(var(--c-secondary) / .3), 0 16px 44px rgba(0,0,0,.55)",
          }}
        >
          {Array.from({ length: 64 }, (_, d) => {
            const displayRow = Math.floor(d / 8);
            const displayCol = d % 8;
            // From `you`'s perspective: White (0) sees rank 1 at the bottom; Black (1)
            // sees the board flipped (rank 8 at the bottom, files mirrored).
            const rank = you === 0 ? 7 - displayRow : displayRow;
            const file = you === 0 ? displayCol : 7 - displayCol;
            const square = `${FILES[file] ?? "a"}${rank + 1}`;
            const piece = view.board[rank * 8 + file] ?? null;
            const isLight = (file + rank) % 2 === 1;
            const isTarget = targets.includes(square);
            // Per-square motion (see SquareFx): the mover / castle-rook slide in, a promotion
            // pops in, the mated king desaturates + topples, the checked king's glow pulses.
            const slide =
              square === lastMove?.to && moverSlide
                ? moverSlide
                : castleRook && square === castleRook.to && rookSlide
                  ? rookSlide
                  : undefined;
            const pieceClass =
              square === promoSquare
                ? "chess-promote-in"
                : square === matedKingSquare
                  ? `chess-king-mated${reduced ? "" : " chess-king-topple"}`
                  : undefined;
            const isChecked = view.check === square;
            const fx: SquareFx | undefined =
              slide || pieceClass || isChecked
                ? {
                    slide,
                    moveKey,
                    pieceClass,
                    elevate: slide != null,
                    checkPulse: isChecked && animateMove,
                    checkKey,
                  }
                : undefined;
            return (
              <BoardSquare
                key={square}
                square={square}
                piece={piece}
                isLight={isLight}
                interactive={isMyTurn}
                isSelected={square === selected}
                isTarget={isTarget}
                isCapture={isTarget && piece != null}
                isLastMove={!!lastMove && (square === lastMove.from || square === lastMove.to)}
                isCheck={isChecked}
                fileLabel={displayRow === 7 ? (FILES[file] ?? undefined) : undefined}
                rankLabel={displayCol === 0 ? String(rank + 1) : undefined}
                onTap={onTap}
                fx={fx}
                isShaking={square === shakeSquare}
                pieceSize={pieceSize}
              />
            );
          })}
          {/* Capture ghost — the taken piece shrinks + fades where it stood (§11 #2/#4). It
              sits ABOVE the board yet BELOW the sliding mover (which rides an elevated
              square), and is keyed by the move so it plays once. Not rendered under reduced
              motion (an instant remove). */}
          {capturedGhost ? (
            <span
              key={`ghost-${moveKey}`}
              aria-hidden
              data-capture-ghost={`${capturedGhost.piece.color}${capturedGhost.piece.type}`}
              className="chess-capture-vanish pointer-events-none absolute flex items-center justify-center"
              style={{
                left: `${capturedGhost.col * 12.5}%`,
                top: `${capturedGhost.row * 12.5}%`,
                width: "12.5%",
                height: "12.5%",
                zIndex: 24,
              }}
            >
              <ChessPiece
                type={capturedGhost.piece.type}
                color={capturedGhost.piece.color}
                size={pieceSize}
              />
            </span>
          ) : null}
        </div>
      </main>

      {/* SAN move list — a premium horizontal ribbon of full-move cells (LTR content).
          Each cell groups its Persian move number with White's & Black's SAN as tidy
          monospace tokens; the LATEST ply is gold-highlighted, and the ribbon auto-scrolls
          to the newest move at the right end. RTL-agnostic: `dir="ltr"` keeps the notation
          reading left→right whatever the app's direction. */}
      <div className="mx-auto w-full max-w-[420px] px-3 pt-1">
        <div
          ref={moveListRef}
          dir="ltr"
          aria-label={fa.chess.moves}
          className="flex items-stretch gap-1.5 overflow-x-auto rounded-xl border border-outline-variant bg-surface-container/60 px-2 py-1.5 [&::-webkit-scrollbar]:hidden"
          style={{ minHeight: 40, scrollbarWidth: "none" }}
        >
          {moveRows.length === 0 ? (
            <span
              className="flex w-full items-center justify-center font-caption text-caption text-on-surface-variant"
              style={{ direction: "rtl" }}
            >
              {fa.chess.moves}
            </span>
          ) : (
            moveRows.map((r) => {
              const whitePly = (r.n - 1) * 2; // this row's White ply index into moveList
              return (
                <div
                  key={r.n}
                  className="flex flex-none items-center gap-1.5 rounded-lg bg-surface-container-high/40 px-1.5 py-1"
                >
                  <span
                    className="text-right font-numeral text-caption tabular-nums text-secondary/75"
                    style={{ minWidth: 15, fontWeight: 700 }}
                  >
                    {faNum(r.n)}
                  </span>
                  <MoveToken san={r.w} color="w" latest={whitePly === moveCount - 1} />
                  {r.b ? (
                    <MoveToken san={r.b} color="b" latest={whitePly + 1 === moveCount - 1} />
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Own strip — more space above it (pt-4, between the move list and the profile) and lifted
          off the bottom edge (pb 24) so the profile + captured box sit a touch higher. */}
      <div
        className="px-4 pt-4"
        style={{
          paddingBottom: "calc(24px + var(--game-safe-bottom))",
        }}
      >
        <PlayerStrip
          name={ownName}
          avatarId={ownAvatarId}
          avatarPhotoId={ownAvatarPhoto}
          official={self?.isOfficial === true}
          tray={myTray}
          trayColor={oppColor}
          advantage={advantage}
          active={isMyTurn}
          clock={myClock}
        />
      </div>

      {/* Promotion picker (mover's colour). */}
      {promo ? (
        <PromoPicker color={myColor} onPick={pickPromo} onCancel={() => setPromo(null)} />
      ) : null}
    </div>
  );
}
